import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  label?: string;
  id?: string;
  className?: string;
  placeholder?: string;
  required?: boolean;
}

const toDisplayFormat = (isoDate: string): string => {
  const [yyyy, mm, dd] = isoDate.split("-");
  return `${dd}-${mm}-${yyyy}`;
};

const toNativeFormat = (customDate: string): string => {
  const [dd, mm, yyyy] = customDate.split("-");
  return `${yyyy}-${mm}-${dd}`;
};

const isValidDateFormat = (value: string): boolean => {
  return /^\d{2}-\d{2}-\d{4}$/.test(value);
};

export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  error,
  label = "Date",
  id = "date",
  className,
  placeholder = "dd-mm-yyyy",
  required = false,
}) => {
  const [inputValue, setInputValue] = useState<string>(value ? value : "");
  const [inputError, setInputError] = useState<string | undefined>(error);

  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value);
    }
  }, [value, inputValue]);

  useEffect(() => {
    setInputError(error);
  }, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nativeValue = e.target.value;
    const formatted = toDisplayFormat(nativeValue);
    setInputValue(formatted);
    onChange(formatted);
    setInputError(undefined);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className='ml-1 text-destructive'>*</span>}
        </Label>
      )}
      <Input
        id={id}
        type='date'
        value={isValidDateFormat(inputValue) ? toNativeFormat(inputValue) : ""}
        onChange={handleChange}
        required={required}
        className='w-full'
      />
      {inputError && <p className='text-sm text-destructive'>{inputError}</p>}
    </div>
  );
};
